import { inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import {
  MenuData,
  MenuItemsResponse,
} from '../layouts/types/menu-items-response';
import {
  AdminLinkActionType,
  UserPermission,
  UserPermissionResponse,
} from '../types';
import { ApiService } from '../../shared/services';
import { InjectableApi } from '../../shared/decorators';
import { ForceAny } from '../../shared/types';
import { MenuListItem } from '../modals/navigation.modal';
import { USER_LEVEL_ENUM } from '../types/user-profile.type';
import { ADMIN_LINKS_ACTIONS } from '../types/permission.type';

@InjectableApi({ providedIn: 'root', baseSegment: '' })
export class NavigationService extends ApiService {
  public navigationData: ForceAny[] = [];
  public userData: ForceAny = null;
  sublinkMap = new Map<string, number>(); // used for dynamic audit trail routing
  isInitialized = false;

  //Permissions
  userPermissionData: Map<number | string, UserPermission> = new Map(); // link id to permission object map
  currentStatusPemissionData: Map<number | string, UserPermission> = new Map();
  userCurrentStatusPermissionData: Map<number | string, UserPermission> =
    new Map();

  public userLogo: string = '';
  public breadcrumbInstance: ForceAny = null;
  public subRouteLinks: string[] = [];
  public $breadcrumbLabelListener = new BehaviorSubject<ForceAny>(null);
  public clientDisabilityChanges = new BehaviorSubject<ForceAny>(null);
  private router = inject(Router);

  getChildSubLinks(): string[] {
    return [];
  }

  isSuperAdmin(): boolean {
    return localStorage.getItem('userLevel') === USER_LEVEL_ENUM.SUPER_ADMIN;
  }

  hasLinkPermission(
    linkId?: number | string | null,
    actions: AdminLinkActionType[] = [],
  ): boolean {
    if (!linkId) {
      return true;
    }

    if (this.isSuperAdmin()) {
      return true;
    }

    const permission = this.userPermissionData.get(Number(linkId));

    if (!permission) {
      return false;
    }

    if (!actions.length) {
      return true;
    }

    return (
      permission.action.includes(ADMIN_LINKS_ACTIONS.ALL) ||
      actions.some((action) => permission.action.includes(action))
    );
  }

  filterItemsByPermission<
    T extends {
      id?: number | string;
      permissionAction?: AdminLinkActionType[];
    },
  >(items: T[]): T[] {
    if (this.isSuperAdmin()) {
      return items;
    }

    return items.filter((item) =>
      this.hasLinkPermission(item.id, item.permissionAction),
    );
  }

  hasPermissionForRoute(): boolean {
    return true;
  }

  navigate(path: string): void {
    this.router.navigate([path]);
  }

  getExactRouteSublink(): number | null {
    const match = this.sublinkMap.get(window.location.pathname);
    if (match) return match;
    return null;
  }

  initPermissionData(permissions: UserPermissionResponse) {
    // save the user permission so provide tab permission in each component
    const permissionData = [
      ...(permissions.data?.ADMIN_LINKS ?? []),
      ...(permissions.data?.CONTROLS ?? []),
      ...(permissions.data?.ENHANCED_SERVICE ?? []),
      ...(permissions.data?.FSCC ?? []),
    ];
    this.initUserPermissionData(permissionData);

    this.navigationData.forEach((menuItem) => {
      this.addPermissionsToMenuItem(menuItem, permissionData);
    });
  }

  addPermissionsToMenuItem(menuItem: MenuData, permissions: UserPermission[]) {
    // Find the matching permission based on linkId
    const matchedPermission = permissions.find(
      (permission) => +permission.linkId === menuItem.id,
    );

    if (matchedPermission) {
      menuItem.actions = matchedPermission.action; // Assuming operations is a string of actions
    }

    if (menuItem.childLinkIds && menuItem.childLinkIds.length > 0) {
      menuItem.childLinkIds = menuItem.childLinkIds.filter((childId) =>
        permissions.some((permission) => +permission.linkId === childId),
      );
    }

    // If the menu item has sublinks, repeat the process recursively
    if (menuItem && menuItem?.sublinks && menuItem.sublinks.length > 0) {
      menuItem.sublinks.forEach((subMenuItem) => {
        this.addPermissionsToMenuItem(subMenuItem, permissions);
      });
    }
  }

  initNavigationData(navData: MenuItemsResponse) {
    this.navigationData = navData.data.reduce((acc, data) => {
      if (data.linkName === 'Logoff' || !data.isDisplayInMenu) return acc;
      acc.push(new MenuListItem(data, '', this.sublinkMap, this.subRouteLinks));
      return acc;
    }, [] as MenuListItem[]);

    this.isInitialized = true;
  }

  resetNavigationData() {
    this.userPermissionData.clear();
    this.isInitialized = false;
  }

  initUserPermissionData(userPermission: UserPermission[]) {
    userPermission.forEach((permisssion) => {
      this.userPermissionData.set(Number(permisssion.linkId), permisssion);
    });
  }

  isDataInitialized(): boolean {
    return this.isInitialized;
  }

  // Returns Actions only for exact same route
  getActionByLinkId() {
    const id = this.getExactRouteSublink();
    if (!id) {
      return null;
    }
    const findActions = (
      menuItems: MenuData[],
      linkId: number,
    ): AdminLinkActionType[] | null => {
      for (const menuItem of menuItems) {
        if (menuItem.id === linkId) {
          return menuItem.actions || null;
        }

        // Recursively check the sublinks if they exist
        if (menuItem.sublinks && menuItem.sublinks?.length > 0) {
          const actions = findActions(menuItem.sublinks, linkId);
          if (actions) {
            return actions;
          }
        }
      }
      return null;
    };

    // Convert id string to number and search for the actions in the navigation data
    return findActions(this.navigationData, id);
  }
}
